import { StreamLanguage } from "@codemirror/language"
import { shell } from "@codemirror/legacy-modes/mode/shell"
import { styled } from "@linaria/react"
import { useEffect, useState } from "react"

import { Button } from "@/components/Button/Button.tsx"
import { CodeMirror } from "@/components/CodeMirror/CodeMirror.tsx"
import { Dialog } from "@/components/Dialog/Dialog.tsx"
import { Horizontal, Spring, Vertical } from "@/components/Flex.ts"
import { FormField } from "@/components/FormField/FormField.tsx"
import { Input } from "@/components/Input/Input.tsx"
import { inputBaseStyles, inputFocusStyles } from "@/styles/inputMixins.ts"
import * as textStyles from "@/styles/textStyles.ts"
import { ellipsize } from "@/styles/textStyles.ts"

const ID_REGEX = /^[a-z-]{1,32}$/

interface AddPreviewScriptDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  isReadonly: boolean
  onAdd: (id: string, title: string, script: string) => Promise<void> | void
}

export const AddPreviewScriptDialog = ({ open, onOpenChange, isReadonly, onAdd }: AddPreviewScriptDialogProps) => {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      trigger={null}
      title="Add Preview Run Script"
    >
      <AddPreviewScriptDialogContent open={open} onOpenChange={onOpenChange} isReadonly={isReadonly} onAdd={onAdd} />
    </Dialog>
  )
}

const AddPreviewScriptDialogContent = ({ open, onOpenChange, isReadonly, onAdd }: AddPreviewScriptDialogProps) => {
  const [id, setId] = useState("")
  const [title, setTitle] = useState("")
  const [script, setScript] = useState("")
  const [submitInProgress, setSubmitInProgress] = useState(false)

  const isIdValid = ID_REGEX.test(id)
  const canSubmit = !isReadonly && isIdValid && title.trim().length > 0 && script.trim().length > 0

  useEffect(() => {
    if (!open) {
      setId("")
      setTitle("")
      setScript("")
    }
  }, [open])

  return (
    <FullWidthVertical alignItems="stretch" gap="12px">
      <FormField
        label="Command ID"
        description="Must contain only lowercase Latin letters and dashes, maximum 32 characters (e.g. start-web)"
      >
        <InputContainer>
          <FullWidthInput
            value={id}
            onChange={(e) => setId(e.target.value)}
            placeholder="Enter command ID"
            autoFocus={!isReadonly}
            disabled={isReadonly}
          />
          {!isIdValid && id.length > 0 && (
            <ValidationMessage>
              ID must contain only lowercase Latin letters and dashes, maximum 32 characters
            </ValidationMessage>
          )}
        </InputContainer>
      </FormField>
      <FormField label="Title" description="Human-friendly name shown in the UI">
        <InputContainer>
          <FullWidthInput
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Web App"
            disabled={isReadonly}
          />
        </InputContainer>
      </FormField>
      <FormField label="Script" description="Shell command that starts your service">
        <ScriptContainer>
          <CodeMirror
            value={script}
            onChange={setScript}
            basicSetup={{
              foldGutter: false,
              highlightActiveLine: !isReadonly,
              highlightActiveLineGutter: !isReadonly
            }}
            extensions={[StreamLanguage.define(shell)]}
            readOnly={isReadonly}
          />
        </ScriptContainer>
      </FormField>
      <Horizontal alignItems="center" gap="8px">
        <Spring />
        <Button kind="alternative" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button kind="primary" disabled={!canSubmit || submitInProgress} onClick={async () => {
          try {
            setSubmitInProgress(true)
            await onAdd(id.trim(), title.trim(), script)
            onOpenChange(false)
          } finally {
            setSubmitInProgress(false)
          }
        }}>{submitInProgress ? ellipsize("Adding") : "Add"}</Button>
      </Horizontal>
    </FullWidthVertical>
  )
}

const InputContainer = styled(Vertical)`
  position: relative;
  width: 100%;
`

const FullWidthVertical = styled(Vertical)`
  width: 100%;
`

const FullWidthInput = styled(Input)`
  width: 100%;
`

const ValidationMessage = styled.div`
  ${textStyles.smallFontSize};
  color: var(--error-color);
  margin-top: 4px;
`

const ScriptContainer = styled.div`
  position: relative;
  overflow: hidden;

  width: 100%;
  max-width: 100%;
  min-width: 0;
  height: 300px;

  ${inputBaseStyles};

  &:has(.cm-editor.cm-focused) {
    ${inputFocusStyles};
  }

  .cm-editor {
    width: 100%;
    height: 300px;
  }

  .cm-scroller {
    overflow-x: auto;
  }
`
